import { IBloodHome } from '@src/interfaces/blood';
import { devices } from '@src/styles/theme';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

/**
 * 헌혈의집 카드 (디자인 시스템 적용).
 * 좌: 혈액원 뱃지 + 이름 + 주소 / 우: 평점·리뷰 수 + 리뷰 보기
 */
type Props = {
  home: IBloodHome;
};

const BANK_LABEL: Record<string, string> = {
  RED_CROSS: '대한적십자사',
  HANMAUM: '한마음혈액원',
};

const BloodHomeCard = ({ home }: Props) => {
  const router = useRouter();
  const hasReview = (home?.reviewCount ?? 0) > 0;
  const address = home?.address?.location ?? '';

  const goDetail = () =>
    router.push(
      `/blood-home/reviews?id=${home.uuid}&name=${home.name}&address=${address}`
    );

  return (
    <Card onClick={goDetail}>
      <Body>
        <TopRow>
          <BankBadge>{BANK_LABEL[home?.kind] ?? '헌혈센터'}</BankBadge>
          {hasReview && (
            <Rating>
              ★ {home.gpaAvg}
              <RatingCount>({home.reviewCount})</RatingCount>
            </Rating>
          )}
        </TopRow>
        <Name>{home?.name}</Name>
        {address && <Address>{address}</Address>}
      </Body>
      <Actions onClick={(e) => e.stopPropagation()}>
        <DetailButton onClick={goDetail}>
          {hasReview ? '리뷰 보기' : '첫 리뷰 남기기'}
        </DetailButton>
      </Actions>
    </Card>
  );
};

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 20px;
  padding: 22px 24px;
  cursor: pointer;
  transition: border-color 0.15s;
  &:hover {
    border-color: #ffd3d3;
  }
  @media ${devices.tablet} {
    padding: 16px;
    border-radius: 16px;
    gap: 12px;
  }
`;
const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const BankBadge = styled.span`
  height: 22px;
  padding: 0 9px;
  border-radius: 11px;
  background: #fff1ed;
  color: #ff4651;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  line-height: 1;
`;
const Rating = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 700;
  color: #ff4651;
`;
const RatingCount = styled.span`
  font-weight: 500;
  color: #b7b7b7;
`;
const Name = styled.h3`
  margin: 0;
  font-family: 'Var', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  color: #222222;
  @media ${devices.tablet} {
    font-size: 15px;
    line-height: 22px;
  }
`;
const Address = styled.span`
  font-size: 13.5px;
  color: #767676;
  @media ${devices.tablet} {
    font-size: 12px;
  }
`;
const Actions = styled.div`
  flex: none;
  @media ${devices.tablet} {
    display: none;
  }
`;
const DetailButton = styled.button`
  height: 36px;
  padding: 0 16px;
  border-radius: 18px;
  border: 1px solid #e6e6e6;
  background: #ffffff;
  color: #767676;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    border-color: #ff6969;
    color: #ff4651;
  }
`;

export default BloodHomeCard;
